import type { CSSProperties } from 'react'
import { MapPin, Navigation } from 'lucide-react'
import { clsx } from 'clsx'

// Design-stub map surface — a styled representation, NOT a live map. Reads as a
// dark map (street grid + an accent proximity ring + pins) so a club can see the
// location-first vision. Swap for the real client-side Google map (reuse
// clubmanager's @googlemaps/js-api-loader) when the discovery feature un-gates.
// Deterministic pin positions (no Math.random) keep it stable across renders.
const PIN_SPOTS = [
  { top: '32%', left: '30%' },
  { top: '58%', left: '64%' },
  { top: '40%', left: '76%' },
  { top: '68%', left: '38%' },
  { top: '24%', left: '58%' },
]

export function DesignedMap({
  className,
  pins = 1,
  showCenter = true,
  label,
}: {
  className?: string
  pins?: number
  showCenter?: boolean
  label?: string
}) {
  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--panel-2)]',
        className,
      )}
      role="img"
      aria-label={label ?? 'Mapa de la zona'}
    >
      {/* street grid */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'linear-gradient(var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />
      {/* a couple of accent "avenues" */}
      <div className="absolute inset-0">
        <div className="absolute left-0 right-0 top-[46%] h-[3px] bg-[var(--line-strong)] opacity-70 -rotate-6" />
        <div className="absolute top-0 bottom-0 left-[38%] w-[3px] bg-[var(--line-strong)] opacity-70 rotate-3" />
      </div>

      {/* scattered club pins */}
      {PIN_SPOTS.slice(0, Math.max(0, pins)).map((spot, i) => (
        <span
          key={i}
          className="absolute -translate-x-1/2 -translate-y-full text-[color:var(--accent)] drop-shadow"
          style={spot as CSSProperties}
        >
          <MapPin size={22} fill="var(--accent-soft)" />
        </span>
      ))}

      {/* the user's location — proximity ring + dot at centre */}
      {showCenter && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="absolute h-24 w-24 rounded-full border border-[var(--accent)] opacity-40" />
          <span className="absolute h-14 w-14 rounded-full bg-[var(--accent-wash)]" />
          <span className="relative h-3.5 w-3.5 rounded-full bg-[var(--accent)] ring-4 ring-[var(--accent-wash)]" />
        </div>
      )}

      {/* honest "design stub" affordance */}
      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-[var(--surface-card)] px-2 py-1 text-[length:var(--fs-2xs)] font-semibold text-[color:var(--fg-muted)] border border-[var(--line)]">
        <Navigation size={11} /> Mapa
      </span>
    </div>
  )
}
